import { Injectable } from '@angular/core';

import { SymbologyOverrideChoice } from '../styles/style-symbology';
import { Feature } from './feature';

export class CachedFeature extends Feature {
    editedOn: number;
    deleted: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class CachedFeatureService {
    // feature cache and management methods - most of them are used in feature.symbologyProperties

    private featureIdToFeature: { [key: string]: CachedFeature } = {};

    public getFeature(featureId: string): CachedFeature {
        return this.featureIdToFeature[featureId];
    }

    public addOrUpdateFeature(feature: Feature): void {
        this.featureIdToFeature[feature.id] = feature as CachedFeature;
    }

    removeFeature(featureId: string): void {
        delete this.featureIdToFeature[featureId];
    }

    public cacheIsNewer(featureId: string, time: number): boolean {
        const cachedFeature = this.getFeature(featureId);
        return cachedFeature && cachedFeature.editedOn && cachedFeature.editedOn > time;
    }

    public updateFromCacheFeature(feature: Feature): CachedFeature {
        const cachedFeature = this.getFeature(feature.id);
        let updatedCachedFeature = feature as CachedFeature;

        if (cachedFeature) {
            updatedCachedFeature.inVisibleTask = cachedFeature.inVisibleTask;
            updatedCachedFeature.updated = cachedFeature.updated;
            updatedCachedFeature.selected = cachedFeature.selected;
            updatedCachedFeature.active = cachedFeature.active;
            updatedCachedFeature.editedOn = cachedFeature.editedOn;
            updatedCachedFeature.deleted = cachedFeature.deleted;
        }

        return updatedCachedFeature;
    }

    public storeFeature(feature: Feature, properties: { [key: string]: any }): void {
        const cachedFeature = this.getFeature(feature.id);
        if (cachedFeature) {
            if (
                !cachedFeature.properties ||
                Object.keys(feature.properties || {}).length > Object.keys(cachedFeature.properties || {}).length
            ) {
                cachedFeature.properties = feature.properties;
            }

            // Only set symbologyProperties if they don't already exist
            if (!cachedFeature.symbologyProperties || !Object.keys(cachedFeature.symbologyProperties).length) {
                cachedFeature.symbologyProperties = properties;
            }

            this.featureIdToFeature[feature.id] = cachedFeature;
        } else {
            feature.symbologyProperties = properties;
            this.featureIdToFeature[feature.id] = feature as CachedFeature;
        }
    }

    // --------------
    // feature cache querying methods

    public getMapFeaturesInTask(): CachedFeature[] {
        return Object.keys(this.featureIdToFeature)
            .map(key => this.featureIdToFeature[key])
            .filter(feature => feature.inVisibleTask === true);
    }

    public getFeaturesByOverride(layerId: string, override: SymbologyOverrideChoice): CachedFeature[] {
        return Object.keys(this.featureIdToFeature)
            .map(key => this.featureIdToFeature[key])
            .filter(
                feature =>
                    (feature.layerId === layerId &&
                        feature.symbologyProperties &&
                        feature.symbologyProperties[override.choiceField] === override.choiceValue) ||
                    (feature.layerId === layerId &&
                        feature.properties &&
                        feature.properties[override.choiceField] === override.choiceValue)
            );
    }

    public getFeaturesByLayerId(layerId: string): CachedFeature[] {
        return Object.keys(this.featureIdToFeature)
            .map(key => this.featureIdToFeature[key])
            .filter(feature => feature.layerId === layerId && feature.properties);
    }
    /**
     * Returns features for a given layerId that do NOT match ANY of the (choiceField, choiceValue) pairs in the overrides array.
     */
    public getFeaturesNotInAnyOverride(layerId: string, overrides: SymbologyOverrideChoice[]): CachedFeature[] {
        if (!overrides?.length) return [];
        return Object.keys(this.featureIdToFeature)
            .map(key => this.featureIdToFeature[key])
            .filter(
                feature =>
                    feature.layerId === layerId &&
                    feature.properties &&
                    !overrides.some(override => feature.properties[override.choiceField] === override.choiceValue)
            );
    }
}
